import React, { useState, useEffect, useCallback } from 'react';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import DatenUebernahme from '@/components/shared/DatenUebernahme';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useAuth } from '@/contexts/AuthContext';
import { formatPriceCt } from '@/utils/formatters';
import { apiFetch } from '@/lib/api';
import {
  FiSave, FiDollarSign, FiDatabase, FiCheckCircle, FiXCircle,
  FiAlertTriangle, FiTrendingUp, FiRefreshCw, FiLock,
} from 'react-icons/fi';
import {
  getAufschlagConfig, saveAufschlagConfig, loadAufschlagConfig, getAufschlagStatus,
  DEFAULT_STAFFEL, DEFAULT_FAKTOREN,
} from '@/data/strukturaufschlag';

// Ein Status-Zeile-Zustand: 'checking' | 'ok' | 'warn' | 'error'
const StatusRow = ({ label, state, detail }) => {
  const icon = {
    ok: <FiCheckCircle className="text-green-500" size={16} />,
    warn: <FiAlertTriangle className="text-amber-500" size={16} />,
    error: <FiXCircle className="text-red-500" size={16} />,
    checking: <FiRefreshCw className="text-gray-400 animate-spin" size={16} />,
  }[state] || null;
  const textClass = {
    ok: 'text-green-700',
    warn: 'text-amber-700',
    error: 'text-red-700',
    checking: 'text-gray-500',
  }[state] || 'text-gray-500';

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="flex items-center space-x-2 text-right">
        {icon}
        <span className={`text-sm ${textClass}`}>{detail}</span>
      </div>
    </div>
  );
};

const Settings = () => {
  const { marginSettings, updateMarginSettings, loading: suppliersLoading } = useSuppliers();
  const { isManager, profile } = useAuth();

  // ============================================================
  // Marge
  // ============================================================
  const [margin, setMargin] = useState({
    default_margin_ct_kwh: marginSettings?.default_margin_ct_kwh ?? 1.5,
    min_margin_ct_kwh: marginSettings?.min_margin_ct_kwh ?? 0.5,
    max_margin_ct_kwh: marginSettings?.max_margin_ct_kwh ?? 3.0,
  });
  const [marginSaving, setMarginSaving] = useState(false);
  const [marginError, setMarginError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Die Margen-Einstellungen kommen serverseitig (asynchron) -> nachziehen
  useEffect(() => {
    if (!marginSettings) return;
    setMargin({
      default_margin_ct_kwh: marginSettings.default_margin_ct_kwh ?? 1.5,
      min_margin_ct_kwh: marginSettings.min_margin_ct_kwh ?? 0.5,
      max_margin_ct_kwh: marginSettings.max_margin_ct_kwh ?? 3.0,
    });
  }, [marginSettings]);

  const handleSaveMargin = async () => {
    setMarginSaving(true);
    setMarginError(null);
    try {
      const res = await updateMarginSettings({ ...marginSettings, ...margin });
      if (res && res.ok === false) {
        setMarginError(res.error || 'Margen-Einstellungen konnten nicht gespeichert werden');
        return;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setMarginError('Speichern fehlgeschlagen: ' + err.message);
    } finally {
      setMarginSaving(false);
    }
  };

  // ============================================================
  // Strukturaufschlag (serverseitig, gilt fuer ALLE Vertriebler)
  // ============================================================
  const [aufschlag, setAufschlag] = useState(() => getAufschlagConfig());
  const [aufschlagStatus, setAufschlagStatus] = useState(() => getAufschlagStatus());
  const [aufschlagLoading, setAufschlagLoading] = useState(true);
  const [aufschlagError, setAufschlagError] = useState(null);
  const [aufschlagSaving, setAufschlagSaving] = useState(false);
  const [aufschlagSaved, setAufschlagSaved] = useState(false);

  const fetchAufschlag = useCallback(async () => {
    setAufschlagLoading(true);
    const res = await loadAufschlagConfig({ force: true });
    const status = getAufschlagStatus();
    setAufschlagStatus(status);
    if (res.ok) {
      setAufschlag(getAufschlagConfig());
      setAufschlagError(null);
    } else {
      // Ehrlich melden statt einen Default als "gespeicherten Wert" auszugeben
      setAufschlagError(res.error || 'Strukturaufschlag konnte nicht geladen werden');
    }
    setAufschlagLoading(false);
  }, []);

  useEffect(() => { fetchAufschlag(); }, [fetchAufschlag]);

  const canEditAufschlag = !!isManager && !aufschlagError && !aufschlagLoading;

  const handleSaveAufschlag = async () => {
    if (!canEditAufschlag) return;
    setAufschlagSaving(true);
    setAufschlagError(null);
    const res = await saveAufschlagConfig(aufschlag);
    setAufschlagSaving(false);
    if (!res.ok) {
      setAufschlagError(res.error || 'Speichern fehlgeschlagen');
      return;
    }
    setAufschlag(getAufschlagConfig());
    setAufschlagStatus(getAufschlagStatus());
    setAufschlagSaved(true);
    setTimeout(() => setAufschlagSaved(false), 2000);
  };

  const resetAufschlagToDefault = () => {
    if (!canEditAufschlag) return;
    setAufschlag({ staffel: { ...DEFAULT_STAFFEL }, faktoren: { ...DEFAULT_FAKTOREN } });
  };

  // ============================================================
  // Futures / System-Status (echte Pruefungen, keine Deko-Haken)
  // ============================================================
  const [futures, setFutures] = useState(null);
  const [backendStatus, setBackendStatus] = useState({ state: 'checking', detail: 'Pruefe ...' });
  const [futuresStatus, setFuturesStatus] = useState({ state: 'checking', detail: 'Pruefe ...' });

  const checkSystem = useCallback(async () => {
    setBackendStatus({ state: 'checking', detail: 'Pruefe ...' });
    setFuturesStatus({ state: 'checking', detail: 'Pruefe ...' });

    // Backend + Datenbank: /api/auth/me liest den User aus Postgres
    try {
      const resp = await apiFetch('/api/auth/me');
      if (resp.ok) {
        const me = await resp.json();
        setBackendStatus({
          state: 'ok',
          detail: `Verbunden — angemeldet als ${me.full_name || me.email} (${me.role})`,
        });
      } else if (resp.status === 401 || resp.status === 403) {
        setBackendStatus({ state: 'warn', detail: 'Erreichbar, aber nicht angemeldet' });
      } else {
        setBackendStatus({ state: 'error', detail: `Fehler (HTTP ${resp.status})` });
      }
    } catch (err) {
      setBackendStatus({ state: 'error', detail: 'Nicht erreichbar: ' + err.message });
    }

    // Marktdaten: /api/futures meldet selbst, ob es Live- oder Fallback-Werte sind
    try {
      const resp = await apiFetch('/api/futures');
      if (!resp.ok) {
        setFutures(null);
        setFuturesStatus({ state: 'error', detail: `Nicht verfuegbar (HTTP ${resp.status})` });
      } else {
        const data = await resp.json();
        setFutures(data);
        if (data?.fallback || !data?.stand) {
          setFuturesStatus({ state: 'warn', detail: `Fallback-Werte — ${data?.quelle || 'kein Live-Abruf'}` });
        } else {
          setFuturesStatus({
            state: 'ok',
            detail: `${data.quelle || 'EEX'} — Stand ${new Date(data.stand).toLocaleDateString('de-DE')}`,
          });
        }
      }
    } catch (err) {
      setFutures(null);
      setFuturesStatus({ state: 'error', detail: 'Nicht erreichbar: ' + err.message });
    }
  }, []);

  useEffect(() => { checkSystem(); }, [checkSystem]);

  const aufschlagRow = aufschlagLoading
    ? { state: 'checking', detail: 'Pruefe ...' }
    : aufschlagError
      ? { state: 'error', detail: aufschlagError }
      : aufschlagStatus.isServerValue
        ? {
            state: 'ok',
            detail: `Serverweit gueltig${aufschlagStatus.updated_at ? ' — Stand ' + new Date(aufschlagStatus.updated_at).toLocaleDateString('de-DE') : ''}`,
          }
        : { state: 'warn', detail: 'Noch nicht gepflegt — Default-Staffel aktiv' };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';
  const roClass = 'w-full px-3 py-2 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg text-sm outline-none cursor-not-allowed';
  const fieldClass = canEditAufschlag ? inputClass : roClass;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Einstellungen</h1>
        <p className="text-gray-500 mt-1">Globale Konfiguration der Plattform</p>
      </div>

      {/*
        Datenuebernahme aus der localStorage-Zeit.
        Rendert sich selbst nur, wenn in diesem Browser tatsaechlich Altdaten
        liegen und die Uebernahme noch nicht abgeschlossen ist — sonst null.
        Bewusst ganz oben, damit die Umstellung nicht uebersehen wird.
      */}
      <DatenUebernahme />

      {/* Marge */}
      <Card>
        <div className="flex items-center space-x-2 mb-4">
          <FiDollarSign className="text-primary-600" size={20} />
          <Card.Title>Margen-Einstellungen</Card.Title>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Die Standard-Marge wird bei jedem neuen Angebot vorausgefuellt. Sie kann pro Angebot individuell angepasst werden.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Standard-Marge (ct/kWh)</label>
            <input
              type="number"
              step="0.1"
              value={margin.default_margin_ct_kwh}
              onChange={e => setMargin(m => ({ ...m, default_margin_ct_kwh: Number(e.target.value) }))}
              className={inputClass}
            />
            <p className="text-xs text-gray-400 mt-1">Wird bei neuen Angeboten vorausgefuellt</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mindest-Marge (ct/kWh)</label>
            <input
              type="number"
              step="0.1"
              value={margin.min_margin_ct_kwh}
              onChange={e => setMargin(m => ({ ...m, min_margin_ct_kwh: Number(e.target.value) }))}
              className={inputClass}
            />
            <p className="text-xs text-gray-400 mt-1">Warnung bei Unterschreitung</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Maximal-Marge (ct/kWh)</label>
            <input
              type="number"
              step="0.1"
              value={margin.max_margin_ct_kwh}
              onChange={e => setMargin(m => ({ ...m, max_margin_ct_kwh: Number(e.target.value) }))}
              className={inputClass}
            />
            <p className="text-xs text-gray-400 mt-1">Obergrenze fuer Vertriebler</p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Beispiel bei 250.000 kWh: Gewinn = <strong>{formatPriceCt(margin.default_margin_ct_kwh)} ct x 250.000 = {(margin.default_margin_ct_kwh / 100 * 250000).toLocaleString('de-DE')} EUR/Jahr</strong>
          </div>
          <Button icon={FiSave} onClick={handleSaveMargin} disabled={marginSaving || suppliersLoading}>
            {marginSaving ? 'Speichere ...' : saved ? 'Gespeichert!' : 'Speichern'}
          </Button>
        </div>
        {marginError && (
          <p className="text-sm text-red-600 mt-3">{marginError}</p>
        )}
      </Card>

      {/* Strukturaufschlag (Boersenbasis) */}
      <Card>
        <div className="flex items-center space-x-2 mb-4">
          <FiTrendingUp className="text-primary-600" size={20} />
          <Card.Title>Strukturaufschlag (Boersenbasis)</Card.Title>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Aufschlag auf den EEX-Boersenpreis = realer Energiepreis. Deckt Profilkosten, Ausgleichsenergie
          und Risikopraemie. Kalibriert gegen ESOS24 (~2,9 ct/kWh). Passen Sie ihn an, wenn die Angebotspreise
          von den ESOS24-Preisen abweichen. <strong>Der Wert wird serverseitig gespeichert und gilt fuer alle
          Vertriebler.</strong>
        </p>

        {/* Zustand: laden / Fehler / Berechtigung */}
        {aufschlagLoading && (
          <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 flex items-center gap-2">
            <FiRefreshCw className="animate-spin" size={14} /> Einstellung wird vom Server geladen ...
          </div>
        )}
        {!aufschlagLoading && aufschlagError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center justify-between gap-3">
            <span>{aufschlagError}</span>
            <button onClick={fetchAufschlag} className="px-3 py-1 rounded bg-white border border-red-300 text-xs hover:bg-red-100">
              Erneut laden
            </button>
          </div>
        )}
        {!aufschlagLoading && !aufschlagError && !isManager && (
          <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600 flex items-center gap-2">
            <FiLock size={14} />
            Nur Admin/Manager duerfen den Strukturaufschlag aendern
            {profile?.role ? ` (Ihre Rolle: ${profile.role})` : ''}. Anzeige schreibgeschuetzt.
          </div>
        )}
        {!aufschlagLoading && !aufschlagError && !aufschlagStatus.isServerValue && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            Serverseitig ist noch kein Aufschlag gespeichert — aktuell rechnet die Plattform mit der Default-Staffel.
          </div>
        )}

        {/* Aufschlag-Staffel pro Laufzeit */}
        <p className="text-sm font-medium text-gray-700 mb-2">Aufschlag pro Laufzeit (ct/kWh)</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map(jahre => (
            <div key={jahre}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{jahre} Jahr{jahre > 1 ? 'e' : ''}</label>
              <input
                type="number"
                step="0.05"
                readOnly={!canEditAufschlag}
                disabled={!canEditAufschlag}
                value={aufschlag.staffel[jahre] ?? ''}
                onChange={e => setAufschlag(a => ({ ...a, staffel: { ...a.staffel, [jahre]: Number(e.target.value) } }))}
                className={fieldClass}
              />
            </div>
          ))}
        </div>

        {/* Profil-/Verbrauchsfaktoren */}
        <p className="text-sm font-medium text-gray-700 mb-2 mt-4">Verbrauchs-/Profilfaktoren (ct/kWh)</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">SLP &lt; 50.000 kWh</label>
            <input type="number" step="0.05" readOnly={!canEditAufschlag} disabled={!canEditAufschlag}
              value={aufschlag.faktoren.slp_klein ?? ''}
              onChange={e => setAufschlag(a => ({ ...a, faktoren: { ...a.faktoren, slp_klein: Number(e.target.value) } }))}
              className={fieldClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">SLP 50-100k kWh</label>
            <input type="number" step="0.05" readOnly={!canEditAufschlag} disabled={!canEditAufschlag}
              value={aufschlag.faktoren.slp_mittel ?? ''}
              onChange={e => setAufschlag(a => ({ ...a, faktoren: { ...a.faktoren, slp_mittel: Number(e.target.value) } }))}
              className={fieldClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">SLP &gt; 100.000 kWh</label>
            <input type="number" step="0.05" readOnly={!canEditAufschlag} disabled={!canEditAufschlag}
              value={aufschlag.faktoren.slp_gross ?? ''}
              onChange={e => setAufschlag(a => ({ ...a, faktoren: { ...a.faktoren, slp_gross: Number(e.target.value) } }))}
              className={fieldClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">RLM</label>
            <input type="number" step="0.05" readOnly={!canEditAufschlag} disabled={!canEditAufschlag}
              value={aufschlag.faktoren.rlm ?? ''}
              onChange={e => setAufschlag(a => ({ ...a, faktoren: { ...a.faktoren, rlm: Number(e.target.value) } }))}
              className={fieldClass} />
          </div>
        </div>

        {/* EEX-Vorschau + resultierender Preis */}
        {futures?.prices && (
          <div className="mt-5 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Vorschau (50.000 kWh SLP, ab naechstem Jahr)</p>
              <span className="text-xs text-gray-400">
                EEX {futures.stand ? new Date(futures.stand).toLocaleDateString('de-DE') : 'Fallback-Werte'}
              </span>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-sm">
              {[1, 2, 3, 4, 5].map(jahre => {
                const startY = new Date().getFullYear() + 1;
                const years = [];
                for (let y = startY; y < startY + jahre; y++) years.push(y);
                const vals = years.map(y => futures.prices[String(y)]).filter(v => v != null);
                const base = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
                const auf = (aufschlag.staffel[jahre] ?? 2.9) + (aufschlag.faktoren.slp_mittel ?? 0.2);
                const energie = base + auf;
                return (
                  <div key={jahre} className="bg-white rounded p-2 text-center border">
                    <p className="text-xs text-gray-400">{jahre}J (bis {startY + jahre - 1})</p>
                    <p className="font-bold text-teal-700">{energie.toFixed(2)}</p>
                    <p className="text-xs text-gray-400">ct/kWh Energie</p>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Energiepreis = EEX-Baseload (gemischt) + Aufschlag. Netzentgelte, Abgaben &amp; Provision kommen hinzu.
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          {canEditAufschlag && (
            <button
              onClick={resetAufschlagToDefault}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Auf Default zuruecksetzen
            </button>
          )}
          <Button icon={FiSave} onClick={handleSaveAufschlag} disabled={!canEditAufschlag || aufschlagSaving}>
            {aufschlagSaving ? 'Speichere ...' : aufschlagSaved ? 'Gespeichert!' : 'Speichern'}
          </Button>
        </div>
      </Card>

      {/* System-Status (echte Pruefungen) */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <FiDatabase className="text-primary-600" size={20} />
            <Card.Title>System-Status</Card.Title>
          </div>
          <button
            onClick={checkSystem}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          >
            <FiRefreshCw size={14} /> Neu pruefen
          </button>
        </div>
        <div className="space-y-3">
          <StatusRow label="Backend / Datenbank" state={backendStatus.state} detail={backendStatus.detail} />
          <StatusRow label="Marktdaten (EEX-Futures)" state={futuresStatus.state} detail={futuresStatus.detail} />
          <StatusRow label="Strukturaufschlag" state={aufschlagRow.state} detail={aufschlagRow.detail} />
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Geprueft wird live gegen /api/auth/me und /api/futures — es werden keine Zustaende angezeigt, die nicht getestet wurden.
        </p>
      </Card>
    </div>
  );
};

export default Settings;
